<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LocationsMap from 'shared/components/map/LocationsMap.vue';

const props = defineProps({
  locations: {
    type: Array,
    default: () => [],
  },
  spaces: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  selectedStage: {
    type: String,
    default: 'all',
  },
  selectedSpaceId: {
    type: [String, Number],
    default: 'all',
  },
});

const { t } = useI18n();
const i18nKey = 'EVENTS_REPORTS';

const STAGE_COLORS = {
  inquiry: '#2563EB',
  confirmed: '#16A34A',
  completed: '#7045AE',
  lost: '#DC2626',
  draft: '#94A3B8',
  archived: '#64748B',
};

const STAGE_LEGEND = [
  { id: 'inquiry', labelKey: 'INQUIRIES' },
  { id: 'confirmed', labelKey: 'CONFIRMED' },
  { id: 'completed', labelKey: 'COMPLETED' },
  { id: 'lost', labelKey: 'DEALS_LOST' },
  { id: 'draft', labelKey: 'DRAFTS' },
];

const stageColor = stage => STAGE_COLORS[stage] || '#64748B';

const filteredLocations = computed(() => {
  let list = props.locations;
  if (props.selectedStage !== 'all') {
    list = list.filter(
      location => location.system_stage === props.selectedStage
    );
  }
  if (props.selectedSpaceId !== 'all') {
    list = list.filter(location =>
      (location.space_ids || []).includes(props.selectedSpaceId)
    );
  }
  return list;
});

const visibleSpaces = computed(() => {
  if (props.selectedSpaceId === 'all') return props.spaces;
  return props.spaces.filter(space => space.id === props.selectedSpaceId);
});

const mapPins = computed(() =>
  filteredLocations.value.map(location => ({
    lat: location.lat,
    lng: location.lng,
    title: location.contact_name,
    subtitle: location.company_name,
    meta: location.location_label,
    body: location.event_name,
    stage: location.system_stage,
    color: stageColor(location.system_stage),
  }))
);

const legendItems = computed(() => {
  if (props.selectedStage !== 'all') {
    const item = STAGE_LEGEND.find(entry => entry.id === props.selectedStage);
    return item
      ? [
          {
            id: item.id,
            label: t(`${i18nKey}.${item.labelKey}`),
            color: stageColor(item.id),
          },
        ]
      : [];
  }
  const present = new Set(
    filteredLocations.value.map(location => location.system_stage)
  );
  return STAGE_LEGEND.filter(item => present.has(item.id)).map(item => ({
    id: item.id,
    label: t(`${i18nKey}.${item.labelKey}`),
    color: stageColor(item.id),
  }));
});

const hasMapContent = computed(
  () => mapPins.value.length > 0 || visibleSpaces.value.length > 0
);
</script>

<template>
  <div
    class="p-6 rounded-xl shadow outline-1 outline outline-n-container bg-n-solid-2"
  >
    <div class="mb-4">
      <h4 class="text-lg font-medium text-n-slate-12 mb-0">
        {{ t(`${i18nKey}.CONTACT_LOCATIONS`) }}
      </h4>
    </div>

    <div
      v-if="loading"
      class="flex items-center justify-center h-80 text-n-slate-11"
    >
      {{ t(`${i18nKey}.MAP_LOADING`) }}
    </div>

    <div
      v-else-if="!hasMapContent"
      class="flex items-center justify-center h-80 text-n-slate-11"
    >
      {{ t(`${i18nKey}.MAP_EMPTY`) }}
    </div>

    <LocationsMap
      v-else
      :locations="mapPins"
      :spaces="visibleSpaces"
      show-legend
      :legend-items="legendItems"
      :space-marker-label="t(`${i18nKey}.MAP_SPACE_MARKER`)"
      :basemap-label="t(`${i18nKey}.MAP_BASEMAP`)"
      :calm-label="t(`${i18nKey}.MAP_BASEMAP_CALM`)"
      :standard-label="t(`${i18nKey}.MAP_BASEMAP_STANDARD`)"
      map-class="h-80"
    />
  </div>
</template>
